import { useMemo, useState } from "react";
import cn from "classnames";

import PriceRange from "./lib/priceRange";
import styles from "./Filters.module.scss";
import capitalize from "../../utils/capitalize";
import {
  convertCategory,
  convertPrice,
  SIZES,
  convertSize,
  GENDERS,
  convertGender,
  HYPOALLERGENICS,
  convertHypoallergenic,
  COLORS,
  convertColor,
  mapOptions,
} from "./lib/utils";
import Link from "next/link";
import TextBody from "../TextBody/TextBody";

const ActiveFilters = ({ variables, setNewRoute }) => {
  const extraKeys = [
    "frontendConfigData",
    "page",
    "breedSlug",
    "sort",
    "state",
    "utm_campaign",
    "utm_medium",
    "utm_source",
    "_kx",
  ];

  const helpers = {
    category: convertCategory,
    size: convertSize,
    color: convertColor,
    price: convertPrice,
    convertGender,
    hypoallergenic: convertHypoallergenic,
  };

  const filters = Object.keys(variables)
    .filter(
      (v) =>
        !extraKeys.some((_k) => _k === v) &&
        !(v === "price" && variables[v] === 11)
    )
    .map((k) => ({
      key: k,
      value: helpers[k]?.(variables[k]) || variables[k],
    }));

  if (!filters.length) return null;

  return (
    <div className={styles.container}>
      <div className={cn(styles.row, styles.mobile)}>
        <TextBody size="s" weight="bold">
          Active Filters
        </TextBody>

        <Link href={"/search"} className={styles.clearAll}>
          <TextBody size="s">Clear All</TextBody>
        </Link>
      </div>

      <div className={styles.activeFilters}>
        {filters.map((f, i) => (
          <div key={`active-filter-${i}`} className={styles.selected}>
            <div className={styles.label}>{f.value}</div>

            <div
              className={styles.clear}
              onClick={() => setNewRoute(f.key, null)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const OptionHeader = ({
  _key,
  title,
  hasValue,
  focusOn,
  setFocusOn,
  isTablet,
}) => (
  <div
    className={styles.title}
    onClick={() => setFocusOn((curr) => ({ ...curr, [_key]: !curr[_key] }))}
  >
    <h4>{title}</h4>

    {!isTablet && (
      <div className={styles.showOptions}>
        <div className={cn(hasValue ? styles.select : null)} />

        {focusOn[_key] ? (
          <img src="/search/arrow-top-black.svg" alt="black arrow" />
        ) : (
          <img src="/search/arrow-down-black.svg" alt="black arrow" />
        )}
      </div>
    )}
  </div>
);

const Option = ({ _key, value, label, isSelected, setNewRoute, isTablet }) => (
  <div className={styles.option} onClick={() => setNewRoute(_key, value)}>
    {isTablet ? (
      <div
        className={cn(
          isSelected ? styles.selectedMobile : styles.notSelectedMobile
        )}
      >
        {label}
      </div>
    ) : (
      <>
        <div
          className={cn(
            styles.checkbox,
            isSelected ? styles.selected : styles.notSelected
          )}
        />

        <p>{label}</p>
      </>
    )}
  </div>
);

const MultipleOption = ({ _key, value, options, setNewRoute, isTablet }) => {
  const values = value?.split(",") || [];

  return (
    <>
      {options.map((o, i) => (
        <div
          key={`option-${i}`}
          className={styles.option}
          onClick={() => {
            if (!o.value) return setNewRoute(_key, o.value);

            setNewRoute(
              _key,
              (values.includes(o.value)
                ? values.filter((v) => v !== o.value)
                : [...values, o.value]
              ).join(",")
            );
          }}
        >
          {isTablet ? (
            <div
              className={cn(
                (!values.length && !o.value) || values?.includes(o.value)
                  ? styles.selectedMobile
                  : styles.notSelectedMobile
              )}
            >
              {o.value && o.icon && (
                <img
                  src={o.icon}
                  style={{ marginRight: "5px" }}
                  alt="option icon"
                />
              )}

              {capitalize(o.label)}
            </div>
          ) : (
            <>
              <div
                className={cn(
                  styles.checkboxMulti,
                  (!values.length && !o.value) || values?.includes(o.value)
                    ? styles.selected
                    : styles.notSelected
                )}
              />

              <p>{o.label}</p>
            </>
          )}
        </div>
      ))}
    </>
  );
};

const Filters = ({
  price,
  size,
  gender,
  hypoallergenic,
  color,
  variables,
  setNewRoute,
  cardsAmount,
  setShowFilters,
  isTablet,
}) => {
  const [focusOn, setFocusOn] = useState({
    price: true,
    size: false,
    gender: false,
    hypoallergenic: false,
    color: false,
  });

  const { sizeOptions, genderOptions, hypoallergenicOptions, colorOptions } =
    useMemo(
      () => ({
        sizeOptions: mapOptions({ optionsDict: SIZES, allowAll: true }),
        genderOptions: mapOptions({ optionsDict: GENDERS, allowAll: true }),
        hypoallergenicOptions: mapOptions({
          optionsDict: HYPOALLERGENICS,
          allowAll: true,
        }),
        colorOptions: mapOptions({ optionsDict: COLORS, allowAll: true }),
      }),
      []
    );

  return (
    <>
      <div
        id="container"
        className={cn(styles.filtersContainer, {
          [styles.auto]: cardsAmount > 7,
          [styles.large]: cardsAmount === 7,
          [styles.medium]: [6, 5].includes(cardsAmount),
          [styles.short]: cardsAmount < 5,
        })}
      >
        <div className={styles.header}>
          <h4>Filter by:</h4>

          {isTablet ? (
            <div
              className={styles.closeButton}
              onClick={() => setShowFilters(false)}
            >
              <img src="/close-modal-grey.svg" alt="close icon" />
            </div>
          ) : (
            <Link href={"/search"} className={styles.clearAll}>
              <TextBody size="s">Clear All</TextBody>
            </Link>
          )}
        </div>

        <div id="filter" className={styles.sectionsContainer}>
          <ActiveFilters {...{ variables, setNewRoute }} />

          <div className={styles.section}>
            <OptionHeader
              _key="gender"
              title="Gender"
              hasValue={!!gender}
              {...{ focusOn, setFocusOn, isTablet }}
            />

            {(focusOn.gender || isTablet) && (
              <div className={styles.optionsContainer}>
                {genderOptions.map(({ value, label }, i) => (
                  <Option
                    key={`gender-option-${i}`}
                    _key="gender"
                    isSelected={gender === value}
                    {...{ value, label, setNewRoute, isTablet }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={styles.section}>
            <OptionHeader
              _key="price"
              title="Price Range"
              hasValue={price !== 11}
              {...{ focusOn, setFocusOn, isTablet }}
            />

            {(focusOn.price || isTablet) && (
              <div className={styles._optionsContainer}>
                <PriceRange values={[price || 11]} setNewRoute={setNewRoute} />
              </div>
            )}
          </div>

          <div className={styles.section}>
            <OptionHeader
              _key="size"
              title="Size"
              hasValue={!!size}
              {...{ focusOn, setFocusOn, isTablet }}
            />

            {(focusOn.size || isTablet) && (
              <div className={styles.optionsContainer}>
                <MultipleOption
                  _key="size"
                  value={size}
                  options={sizeOptions}
                  {...{ setNewRoute, isTablet }}
                />
              </div>
            )}
          </div>

          <div className={styles.section}>
            <OptionHeader
              _key="hypoallergenic"
              title="Allergy"
              hasValue={!!hypoallergenic}
              {...{ focusOn, setFocusOn, isTablet }}
            />

            {(focusOn.hypoallergenic || isTablet) && (
              <div className={styles.optionsContainer}>
                {hypoallergenicOptions.map(({ value, label }, i) => (
                  <Option
                    key={`hypoallergenic-option-${i}`}
                    _key="hypoallergenic"
                    isSelected={hypoallergenic === (value ? !!value : value)}
                    {...{ value, label, setNewRoute, isTablet }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={styles.section}>
            <OptionHeader
              _key="color"
              title="Color"
              hasValue={!!color}
              {...{ focusOn, setFocusOn, isTablet }}
            />

            {(focusOn.color || isTablet) && (
              <div className={styles.optionsContainer}>
                <MultipleOption
                  _key="color"
                  value={color}
                  options={colorOptions}
                  {...{ setNewRoute, isTablet }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={styles.background}
        onClick={() => isTablet && setShowFilters(false)}
      />
    </>
  );
};

export default Filters;
